/**
 * Showing who is working on what.
 *
 * Rendered as a generated stylesheet rather than as state on the nodes, deliberately: writing a
 * class onto the node array puts a write inside an update, and this canvas has produced
 * effect_update_depth_exceeded from exactly that shape — a card re-renders, xyflow writes its
 * measurement back into the array, and round it goes. Nodes carry data-id in the DOM, so a few
 * generated selectors do the same job and cannot participate in the update cycle at all.
 *
 * Pure, so the part that is easy to get wrong — building selectors and CSS strings out of names
 * and ids that came off the network — is testable rather than hopeful.
 */

/** "Roobina Navassanian" → "RN". Two letters is a person; three is a filing code. */
export function initialsOf(name) {
    return String(name ?? '?')
        .trim().split(/\s+/).slice(0, 2)
        .map((part) => part[0] ?? '')
        .join('')
        .toUpperCase() || '?';
}

/**
 * Escape a value for use inside a double-quoted CSS string or attribute selector.
 *
 * Names and ids arrive over a socket from another person's browser. A quote in either would
 * end the string early and turn the rest of the stylesheet into garbage — which, in a
 * stylesheet applied to the whole page, is not a cosmetic failure.
 */
const cssString = (value) => String(value ?? '').replace(/[\\"]/g, '\\$&');

/** A colour is only ever one of ours; anything else is refused rather than interpolated. */
const safeColor = (value) => (/^#[0-9a-fA-F]{3,8}$/.test(String(value ?? '')) ? String(value) : '#6366f1');

/**
 * The stylesheet marking every card somebody else is on.
 *
 * @param {Record<string, {nodeIds: string[], name: string, color: string}>} byUser
 * @returns {string} CSS, or '' when nobody else is on anything
 */
export function workingStyles(byUser) {
    // One card, possibly several people: the badge shows the first and counts the rest,
    // because three overlapping bubbles on a 200px card is worse than a number.
    const byNode = new Map();
    for (const person of Object.values(byUser ?? {})) {
        for (const nodeId of person?.nodeIds ?? []) {
            if (!nodeId) continue;
            if (!byNode.has(nodeId)) byNode.set(nodeId, []);
            byNode.get(nodeId).push(person);
        }
    }

    return [...byNode.entries()].map(([nodeId, people]) => {
        const id = cssString(nodeId);
        const [first, ...rest] = people;
        const label = cssString(initialsOf(first.name) + (rest.length ? `+${rest.length}` : ''));
        const color = safeColor(first.color);

        return `.svelte-flow__node[data-id="${id}"] .research-card {`
            + ` box-shadow: 0 0 0 2px ${color}, 0 2px 10px rgba(0, 0, 0, 0.18);`
            + ' }\n'
            + `.svelte-flow__node[data-id="${id}"]::after {`
            + ` content: "${label}";`
            + ' position: absolute; top: -9px; right: -9px; z-index: 4;'
            + ' display: grid; place-items: center; min-width: 20px; height: 20px;'
            + ' padding: 0 4px; border-radius: 999px; border: 2px solid var(--rc-surface, #fff);'
            + ` background: ${color}; color: #fff;`
            + ' font-size: 9px; font-weight: 700; letter-spacing: 0.02em; pointer-events: none;'
            + ' }';
    }).join('\n');
}

/**
 * Resolve whoever a card is assigned to into people.
 *
 * An assignment arrives two ways and they don't look alike. The server hydrates it into people
 * before the canvas ever sees it; a LIVE assignment arrives as an operation carrying raw user
 * ids, because that is what is stored. Handling only the first is why an assignment showed up
 * instantly for the person making it and not at all for anyone else until they reloaded.
 *
 * @param {Array<number|object>} value  ids, people, or a mix
 * @param {Array<{id: number, name: string, color: string}>} roster
 * @returns {Array<{id: number, name: string, color: string}>}
 */
export function peopleFrom(value, roster) {
    const known = new Map((roster ?? []).map((person) => [Number(person.id), person]));

    const people = (value ?? []).map((entry) => {
        if (entry && typeof entry === 'object') {
            return entry;
        }

        // Somebody the roster doesn't know — a member removed from the team, say. Named
        // honestly rather than dropped: the card IS assigned, and hiding that is worse than
        // admitting we can't say to whom.
        return known.get(Number(entry)) ?? { id: Number(entry), name: 'Someone', color: '#71717a' };
    }).filter(Boolean);

    // One chip per person, whatever the producer sent. The chips are a keyed each, and a
    // duplicate id — [7, 7], or ["7", 7], or two responder profiles resolving to one member —
    // is each_key_duplicate, which takes down the WHOLE canvas, not one card. Boards saved
    // before the server-side dedupe still carry such data, so this heals at read.
    const seen = new Set();

    return people.filter((person) => {
        const key = Number(person.id);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

/**
 * Drop marks whose owner has stopped announcing.
 *
 * The clean goodbye (member_removed, the empty whisper) covers a clean exit. This covers every
 * other one — a killed tab, a dropped socket, a goodbye that never flushed. Marks are
 * heartbeat-refreshed while their owner lives; when the announcements stop, they age out, so a
 * ghost mark is structurally impossible rather than dependent on one event arriving.
 *
 * @param {Record<string, {at?: number}>} byUser
 * @param {number} now
 * @param {number} ttlMs
 * @returns {{next: Record<string, object>, changed: boolean}}
 */
export function sweepStale(byUser, now, ttlMs) {
    const next = {};
    let changed = false;

    for (const [userId, entry] of Object.entries(byUser ?? {})) {
        // An unstamped entry is from before stamping existed; treat it as fresh once, and the
        // next heartbeat stamps it.
        if (entry?.at !== undefined && now - entry.at > ttlMs) {
            changed = true;
            continue;
        }
        next[userId] = entry;
    }

    return { next, changed };
}
